/**
 * Allys Advanced Filters Modal
 * 
 * Comprehensive filter modal with all detailed filtering options
 * Opens when user clicks "Advanced Filters" button
 */

'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AllysFilterPanel, type FilterSection } from './AllysFilterPanel';

// Re-export for use in PropertyListView
export type { FilterSection };

export type AdvancedFilterValues = Record<string, string | string[] | { min?: number; max?: number }>;

export interface AllysAdvancedFiltersModalProps {
  /**
   * Whether modal is open
   */
  isOpen: boolean;
  /**
   * Callback to close modal
   */
  onClose: () => void;
  /**
   * Current filter values
   */
  values: AdvancedFilterValues;
  /**
   * Callback when filters are applied
   */
  onApply: (values: AdvancedFilterValues) => void;
  /**
   * Filter sections configuration
   */
  filterSections: FilterSection[];
  /**
   * Labels for translations
   */
  labels?: {
    title?: string;
    clearAll?: string;
    cancel?: string;
    apply?: string;
    resultsCount?: string;
  };
  /**
   * Number of results that match current filters
   */
  resultsCount?: number;
}

/**
 * AllysAdvancedFiltersModal - Full-featured filter modal
 */
export function AllysAdvancedFiltersModal({
  isOpen,
  onClose,
  values,
  onApply,
  filterSections,
  labels = {
    title: 'Advanced Filters',
    clearAll: 'Clear all',
    cancel: 'Cancel',
    apply: 'Apply Filters',
    resultsCount: 'Show {{count}} properties',
  },
  resultsCount,
}: AllysAdvancedFiltersModalProps) {
  // Internal state for filter values (not applied until user clicks Apply)
  const [internalValues, setInternalValues] = useState<AdvancedFilterValues>(values);
  // Active category for left-side navigation
  const [activeCategory, setActiveCategory] = useState<string>(filterSections[0]?.id || '');
  
  // Sync internal values when prop values change or modal opens
  useEffect(() => {
    if (isOpen) {
      setInternalValues(values);
      // Reset to first category when opening
      setActiveCategory(filterSections[0]?.id || '');
    }
  }, [isOpen, values, filterSections]);
  
  const handleApply = () => {
    onApply(internalValues);
    onClose();
  };
  
  const handleClearAll = () => {
    setInternalValues({});
  };
  
  const hasActiveFilters = Object.keys(internalValues).length > 0;
  
  // Get active filter count per category
  const getCategoryFilterCount = (categoryId: string) => {
    const value = internalValues[categoryId];
    if (!value) return 0;
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) return 1;
    if (typeof value === 'string') return 1;
    return 0;
  };
  
  // Get current active section
  const activeSection = filterSections.find(s => s.id === activeCategory);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col"
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#243462] to-[#2a3d74] px-8 py-6 flex items-center justify-between">
                {/* Decorative gradient accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#243462] via-[#BBA339] to-[#E4CE4A]" />
                
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1 font-inter">
                    {labels.title}
                  </h2>
                  <p className="text-sm text-white/80 font-inter">
                    Refine your property search with detailed filters
                  </p>
                </div>
                
                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>
              
              {/* Filter Content - Two Column Layout */}
              <div className="flex-1 overflow-hidden flex">
                {/* Left Sidebar - Category Navigation */}
                <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                  <div className="p-4">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 px-3 font-inter">
                      Categories
                    </h3>
                    <nav className="space-y-1">
                      {filterSections.map((section) => {
                        const isActive = activeCategory === section.id;
                        const filterCount = getCategoryFilterCount(section.id);
                        
                        return (
                          <motion.button
                            key={section.id}
                            whileHover={{ x: 4 }}
                            onClick={() => setActiveCategory(section.id)}
                            className={`
                              w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-sm font-semibold transition-all font-inter
                              ${isActive 
                                ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white shadow-md' 
                                : 'text-gray-700 hover:bg-white hover:shadow-sm'
                              }
                            `}
                          >
                            <span>{section.label}</span>
                            {filterCount > 0 && (
                              <span className={`
                                px-2 py-0.5 rounded-full text-xs font-bold
                                ${isActive ? 'bg-white/20 text-white' : 'bg-[#BBA339] text-white'}
                              `}>
                                {filterCount}
                              </span>
                            )}
                          </motion.button>
                        );
                      })}
                    </nav>
                  </div>
                </div>
                
                {/* Right Content - Active Filter Section */}
                <div className="flex-1 overflow-y-auto p-8">
                  {activeSection && (
                    <motion.div
                      key={activeCategory}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 font-inter">
                        {activeSection.label}
                      </h3>
                      
                      {/* Filter Content */}
                      <AllysFilterPanel
                        sections={[activeSection]}
                        values={internalValues as Record<string, string | string[] | number | { min?: number; max?: number }>}
                        onChange={(newValues) => setInternalValues(newValues as AdvancedFilterValues)}
                        labels={{
                          title: '',
                          clearAll: '',
                        }}
                        variant="dropdown"
                        className="shadow-none border-0"
                      />
                    </motion.div>
                  )}
                </div>
              </div>
              
              {/* Footer Actions */}
              <div className="border-t-2 border-gray-100 px-8 py-6 bg-gradient-to-b from-white to-gray-50">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {/* Results count indicator */}
                    {resultsCount !== undefined && (
                      <motion.div 
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#243462]/5 to-[#BBA339]/5 rounded-xl border border-gray-100"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] flex items-center justify-center shadow-sm">
                          <span className="text-white font-bold text-xs font-inter">
                            {resultsCount}
                          </span>
                        </div>
                        <span className="text-sm text-gray-700 font-semibold whitespace-nowrap font-inter">
                          {resultsCount === 1 ? 'property' : 'properties'} match
                        </span>
                      </motion.div>
                    )}
                    
                    {/* Clear all button */}
                    {hasActiveFilters && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleClearAll}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all font-inter"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {labels.clearAll}
                      </motion.button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Cancel button */}
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={onClose}
                      className="px-6 py-3 bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-semibold rounded-xl transition-all shadow-sm hover:shadow-md font-inter"
                    >
                      {labels.cancel}
                    </motion.button>
                    
                    {/* Apply button */}
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleApply}
                      className="px-8 py-3 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] hover:from-[#a89333] hover:to-[#d4be45] text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl font-inter"
                    >
                      {labels.apply}
                      <svg className="w-5 h-5 inline-block ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

